import type { FilterType } from '../utils/filters';

export interface HUDCallbacks {
  onOpacityChange?: (value: number) => void;
  onGridToggle?: () => void;
  onFilterCycle?: () => void;
  onStrobeToggle?: () => void;
  onLockToggle?: () => void;
  onAddImage?: () => void;
  onPositionChange?: (dx: number, dy: number) => void;
  onScaleChange?: (delta: number) => void;
  onFlipH?: () => void;
  onFlipV?: () => void;
  onRotationChange?: (delta: number) => void;
}

export class HUD {
  private container: HTMLElement;
  private callbacks: HUDCallbacks;
  private hudElement: HTMLElement | null = null;
  private buttons: Map<string, HTMLElement> = new Map();
  private sliderTrack: HTMLElement | null = null;
  private sliderThumb: HTMLElement | null = null;
  private sliderValue = 0.5;
  private sliderActive = false;
  private tray: HTMLElement | null = null;
  private topRow: HTMLElement | null = null;
  private joystick: HTMLElement | null = null;
  private joystickKnob: HTMLElement | null = null;
  private joystickActive = false;
  private joystickInterval: number | null = null;
  private bottomControls: HTMLElement | null = null;
  private eyeToggle: HTMLElement | null = null;
  private controlsVisible = true;

  constructor(container: HTMLElement, callbacks: HUDCallbacks) {
    this.container = container;
    this.callbacks = callbacks;
    this.render();
    this.attachListeners();
    // HUD icons stay visible - no auto-hide
  }

  private render(): void {
    this.hudElement = document.createElement('div');
    this.hudElement.className = 'hud';

    // Custom vertical opacity slider on RIGHT side (always visible)
    // Using custom implementation instead of native range for better touch support
    const opacityRail = document.createElement('div');
    opacityRail.className = 'hud-opacity-rail';

    this.sliderTrack = document.createElement('div');
    this.sliderTrack.className = 'hud-opacity-track';

    const sliderFill = document.createElement('div');
    sliderFill.className = 'hud-opacity-fill';

    this.sliderThumb = document.createElement('div');
    this.sliderThumb.className = 'hud-opacity-thumb';

    this.sliderTrack.appendChild(sliderFill);
    this.sliderTrack.appendChild(this.sliderThumb);
    opacityRail.appendChild(this.sliderTrack);
    this.hudElement.appendChild(opacityRail);

    // Set initial position
    this.updateSliderVisual();

    // Top row: Joystick + Zoom grouped on LEFT - hidden when eye closed
    const topRow = document.createElement('div');
    topRow.className = 'hud-top-row';

    // Left controls group (joystick + zoom)
    const leftControls = document.createElement('div');
    leftControls.className = 'hud-left-controls';

    // Joystick
    const joystickContainer = document.createElement('div');
    joystickContainer.className = 'hud-joystick-container';

    this.joystick = document.createElement('div');
    this.joystick.className = 'hud-joystick';

    this.joystickKnob = document.createElement('div');
    this.joystickKnob.className = 'hud-joystick-knob';

    this.joystick.appendChild(this.joystickKnob);
    joystickContainer.appendChild(this.joystick);
    leftControls.appendChild(joystickContainer);

    // Scale controls (next to joystick)
    const scaleControls = document.createElement('div');
    scaleControls.className = 'hud-scale-controls';

    const zoomOut = document.createElement('button');
    zoomOut.className = 'hud-button hud-scale-btn';
    zoomOut.textContent = '−';
    zoomOut.setAttribute('data-action', 'zoom-out');

    const zoomIn = document.createElement('button');
    zoomIn.className = 'hud-button hud-scale-btn';
    zoomIn.textContent = '+';
    zoomIn.setAttribute('data-action', 'zoom-in');

    scaleControls.appendChild(zoomOut);
    scaleControls.appendChild(zoomIn);
    leftControls.appendChild(scaleControls);

    // Rotation controls (next to scale)
    const rotationControls = document.createElement('div');
    rotationControls.className = 'hud-rotation-controls';

    const rotateCCW = document.createElement('button');
    rotateCCW.className = 'hud-button hud-rotate-btn';
    rotateCCW.innerHTML = this.getRotateCCWSvg();
    rotateCCW.setAttribute('data-action', 'rotate-ccw');
    rotateCCW.setAttribute('aria-label', 'Rotate counter-clockwise');

    const rotateCW = document.createElement('button');
    rotateCW.className = 'hud-button hud-rotate-btn';
    rotateCW.innerHTML = this.getRotateCWSvg();
    rotateCW.setAttribute('data-action', 'rotate-cw');
    rotateCW.setAttribute('aria-label', 'Rotate clockwise');

    rotationControls.appendChild(rotateCCW);
    rotationControls.appendChild(rotateCW);
    leftControls.appendChild(rotationControls);
    topRow.appendChild(leftControls);

    this.topRow = topRow;
    this.hudElement.appendChild(topRow);

    // Tray row: button tray (centered, toggleable via eye)
    this.tray = document.createElement('div');
    this.tray.className = 'hud-tray';

    const buttonDefs: { action: string; icon: string; useSvg?: boolean }[] = [
      { action: 'lock', icon: '', useSvg: true },
      { action: 'grid', icon: '⊞' },
      { action: 'filter', icon: '◐' },
      { action: 'flipH', icon: '⇆' },
      { action: 'flipV', icon: '⇅' },
      { action: 'add', icon: '', useSvg: true },
    ];

    buttonDefs.forEach(({ action, icon, useSvg }) => {
      const btn = document.createElement('button');
      btn.className = 'hud-button';
      btn.setAttribute('data-action', action);
      if (useSvg) {
        if (action === 'lock') {
          btn.innerHTML = this.getLockOpenSvg();
        } else if (action === 'add') {
          btn.innerHTML = this.getGallerySvg();
        }
      } else {
        btn.textContent = icon;
      }
      this.buttons.set(action, btn);
      this.tray!.appendChild(btn);
    });

    // Bottom row: tray + eye toggle
    const bottomRow = document.createElement('div');
    bottomRow.className = 'hud-bottom-row';

    bottomRow.appendChild(this.tray);

    // Eye toggle (hamburger menu for tray) - always visible
    const persistentControls = document.createElement('div');
    persistentControls.className = 'hud-persistent-controls';

    this.eyeToggle = document.createElement('button');
    this.eyeToggle.className = 'hud-button hud-eye-toggle';
    this.eyeToggle.innerHTML = this.getEyeOpenSvg();
    this.eyeToggle.setAttribute('aria-label', 'Toggle menu');
    persistentControls.appendChild(this.eyeToggle);

    bottomRow.appendChild(persistentControls);

    this.hudElement.appendChild(bottomRow);

    // Keep reference to tray for toggle (not bottomControls anymore)
    this.bottomControls = this.tray;

    this.container.appendChild(this.hudElement);
  }

  private attachListeners(): void {
    // Custom opacity slider with pointer events
    this.setupOpacitySlider();

    // Buttons
    this.buttons.forEach((btn, action) => {
      btn.addEventListener('click', () => {
        switch (action) {
          case 'lock':
            this.callbacks.onLockToggle?.();
            break;
          case 'grid':
            this.callbacks.onGridToggle?.();
            break;
          case 'filter':
            this.callbacks.onFilterCycle?.();
            break;
          case 'flipH':
            this.callbacks.onFlipH?.();
            break;
          case 'flipV':
            this.callbacks.onFlipV?.();
            break;
          case 'add':
            this.callbacks.onAddImage?.();
            break;
        }
      });
    });

    // Scale buttons
    const zoomIn = this.hudElement?.querySelector('[data-action="zoom-in"]');
    const zoomOut = this.hudElement?.querySelector('[data-action="zoom-out"]');

    zoomIn?.addEventListener('click', () => {
      this.callbacks.onScaleChange?.(0.1);
    });

    zoomOut?.addEventListener('click', () => {
      this.callbacks.onScaleChange?.(-0.1);
    });

    // Rotation buttons with tap and hold support
    this.setupRotationButtons();

    // Joystick
    this.setupJoystick();

    // Eye toggle
    this.eyeToggle?.addEventListener('click', () => {
      this.toggleControlsVisibility();
    });
  }

  private toggleControlsVisibility(): void {
    this.controlsVisible = !this.controlsVisible;

    if (this.controlsVisible) {
      // Show all controls with animation
      this.topRow?.classList.remove('hidden');
      this.tray?.classList.remove('hidden');
      this.topRow?.classList.add('waking');
      this.tray?.classList.add('waking');
      this.eyeToggle?.classList.remove('active');

      if (this.eyeToggle) this.eyeToggle.innerHTML = this.getEyeOpenSvg();

      // Remove animation class after completion
      setTimeout(() => {
        this.topRow?.classList.remove('waking');
        this.tray?.classList.remove('waking');
      }, 300);
    } else {
      // Hide topRow and tray - keep only opacity slider + eye toggle visible
      this.topRow?.classList.add('hidden');
      this.tray?.classList.add('hidden');
      this.eyeToggle?.classList.add('active');
      if (this.eyeToggle) this.eyeToggle.innerHTML = this.getEyeClosedSvg();
    }
  }

  // Egyptian-style eye SVG (Eye of Horus inspired, fits in circle)
  private getEyeOpenSvg(): string {
    return `<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <!-- Elongated eye shape -->
      <path d="M2 12C2 12 6 6 12 6C18 6 22 12 22 12C22 12 18 18 12 18C6 18 2 12 2 12Z"
            stroke="currentColor" stroke-width="1.5" fill="none"/>
      <!-- Iris -->
      <circle cx="12" cy="12" r="3" fill="currentColor"/>
      <!-- Egyptian eyelash/brow line extending right -->
      <path d="M19 8L22 5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
    </svg>`;
  }

  private getEyeClosedSvg(): string {
    return `<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <!-- Closed eye line -->
      <path d="M2 12C2 12 6 15 12 15C18 15 22 12 22 12"
            stroke="currentColor" stroke-width="1.5" fill="none" stroke-linecap="round"/>
      <!-- Egyptian line extending right -->
      <path d="M19 14L22 17" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
      <!-- Eyelashes -->
      <path d="M7 14L5 17M10 15L9 18M14 15L15 18M17 14L19 17"
            stroke="currentColor" stroke-width="1" stroke-linecap="round"/>
    </svg>`;
  }

  // Bauhaus-style lock SVG (geometric padlock)
  private getLockOpenSvg(): string {
    return `<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <!-- Open shackle -->
      <path d="M7 10V7C7 4.2 9.2 2 12 2C14.8 2 17 4.2 17 7" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
      <!-- Lock body -->
      <rect x="5" y="10" width="14" height="12" stroke="currentColor" stroke-width="1.5" fill="none"/>
      <!-- Keyhole -->
      <circle cx="12" cy="15" r="1.5" fill="currentColor"/>
      <rect x="11" y="15" width="2" height="4" fill="currentColor"/>
    </svg>`;
  }

  private getLockClosedSvg(): string {
    return `<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <!-- Closed shackle -->
      <path d="M7 10V7C7 4.2 9.2 2 12 2C14.8 2 17 4.2 17 7V10" stroke="currentColor" stroke-width="1.5"/>
      <!-- Lock body -->
      <rect x="5" y="10" width="14" height="12" stroke="currentColor" stroke-width="1.5" fill="none"/>
      <!-- Keyhole -->
      <circle cx="12" cy="15" r="1.5" fill="currentColor"/>
      <rect x="11" y="15" width="2" height="4" fill="currentColor"/>
    </svg>`;
  }

  // Bauhaus-style gallery SVG (geometric landscape/image frame)
  private getGallerySvg(): string {
    return `<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <!-- Picture frame -->
      <rect x="3" y="5" width="18" height="14" stroke="currentColor" stroke-width="1.5" fill="none"/>
      <!-- Mountain silhouette -->
      <path d="M3 16L8 11L12 15L16 10L21 16" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/>
      <!-- Sun circle -->
      <circle cx="17" cy="9" r="2" fill="currentColor"/>
    </svg>`;
  }

  // Rotation counter-clockwise SVG (curved arrow)
  private getRotateCCWSvg(): string {
    return `<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <!-- Curved arrow CCW -->
      <path d="M4 12C4 7.58 7.58 4 12 4C15.37 4 18.24 6.11 19.42 9" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
      <!-- Arrow head -->
      <path d="M4 8V12H8" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
      <!-- Bottom arc (faded) -->
      <path d="M20 12C20 16.42 16.42 20 12 20C8.63 20 5.76 17.89 4.58 15" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" opacity="0.4"/>
    </svg>`;
  }

  // Rotation clockwise SVG (curved arrow)
  private getRotateCWSvg(): string {
    return `<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <!-- Curved arrow CW -->
      <path d="M20 12C20 7.58 16.42 4 12 4C8.63 4 5.76 6.11 4.58 9" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
      <!-- Arrow head -->
      <path d="M20 8V12H16" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
      <!-- Bottom arc (faded) -->
      <path d="M4 12C4 16.42 7.58 20 12 20C15.37 20 18.24 17.89 19.42 15" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" opacity="0.4"/>
    </svg>`;
  }

  /**
   * Apply gaming-style response curve to joystick input
   * - Dead zone: ignore inputs below threshold (prevents drift)
   * - Squared curve: precision at low deflection, speed at high deflection
   */
  private applyResponseCurve(value: number): number {
    const deadZone = 0.15; // 15% dead zone
    const absValue = Math.abs(value);

    // Inside dead zone - no movement
    if (absValue < deadZone) {
      return 0;
    }

    // Remap from [deadZone, 1] to [0, 1]
    const remapped = (absValue - deadZone) / (1 - deadZone);

    // Apply squared curve for precision/speed balance
    // Low deflection = precise, high deflection = fast
    const curved = remapped * remapped;

    // Restore sign
    return value >= 0 ? curved : -curved;
  }

  private updateSliderVisual(): void {
    if (!this.sliderTrack || !this.sliderThumb) return;

    const fill = this.sliderTrack.querySelector('.hud-opacity-fill') as HTMLElement;
    const trackHeight = this.sliderTrack.clientHeight || 200;
    const thumbHeight = 48;

    // Value 1 = top, value 0 = bottom (inverted for visual)
    const thumbY = (1 - this.sliderValue) * (trackHeight - thumbHeight);
    this.sliderThumb.style.top = `${thumbY}px`;

    if (fill) {
      // Fill from bottom up
      fill.style.height = `${this.sliderValue * 100}%`;
    }
  }

  private setupOpacitySlider(): void {
    if (!this.sliderTrack || !this.sliderThumb) return;

    const getValueFromY = (clientY: number): number => {
      const rect = this.sliderTrack!.getBoundingClientRect();
      const thumbHeight = 48;
      const trackHeight = rect.height - thumbHeight;

      // Calculate relative position (0 at bottom, 1 at top)
      const relativeY = clientY - rect.top - thumbHeight / 2;
      const normalizedY = 1 - (relativeY / trackHeight);

      return Math.max(0, Math.min(1, normalizedY));
    };

    const handleStart = (e: PointerEvent) => {
      e.preventDefault();
      e.stopPropagation();
      this.sliderActive = true;
      this.sliderTrack!.classList.add('active');
      this.sliderTrack!.setPointerCapture(e.pointerId);

      this.sliderValue = getValueFromY(e.clientY);
      this.updateSliderVisual();
      this.callbacks.onOpacityChange?.(this.sliderValue);
    };

    const handleMove = (e: PointerEvent) => {
      if (!this.sliderActive) return;
      e.preventDefault();
      e.stopPropagation();

      this.sliderValue = getValueFromY(e.clientY);
      this.updateSliderVisual();
      this.callbacks.onOpacityChange?.(this.sliderValue);
    };

    const handleEnd = (e: PointerEvent) => {
      if (!this.sliderActive) return;
      e.preventDefault();
      this.sliderActive = false;
      this.sliderTrack!.classList.remove('active');
      this.sliderTrack!.releasePointerCapture(e.pointerId);
    };

    // Pointer events (works on most modern browsers)
    this.sliderTrack.addEventListener('pointerdown', handleStart);
    this.sliderTrack.addEventListener('pointermove', handleMove);
    this.sliderTrack.addEventListener('pointerup', handleEnd);
    this.sliderTrack.addEventListener('pointercancel', handleEnd);
    this.sliderTrack.addEventListener('pointerleave', handleEnd);

    // Also handle thumb directly
    this.sliderThumb.addEventListener('pointerdown', handleStart);

    // Touch events as fallback for older Android WebViews
    const handleTouchStart = (e: TouchEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (e.touches.length !== 1) return;

      this.sliderActive = true;
      this.sliderTrack!.classList.add('active');
      this.sliderValue = getValueFromY(e.touches[0].clientY);
      this.updateSliderVisual();
      this.callbacks.onOpacityChange?.(this.sliderValue);
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (!this.sliderActive) return;
      e.preventDefault();
      e.stopPropagation();
      if (e.touches.length !== 1) return;

      this.sliderValue = getValueFromY(e.touches[0].clientY);
      this.updateSliderVisual();
      this.callbacks.onOpacityChange?.(this.sliderValue);
    };

    const handleTouchEnd = (e: TouchEvent) => {
      if (!this.sliderActive) return;
      e.preventDefault();
      this.sliderActive = false;
      this.sliderTrack!.classList.remove('active');
    };

    this.sliderTrack.addEventListener('touchstart', handleTouchStart, { passive: false });
    this.sliderTrack.addEventListener('touchmove', handleTouchMove, { passive: false });
    this.sliderTrack.addEventListener('touchend', handleTouchEnd, { passive: false });
    this.sliderTrack.addEventListener('touchcancel', handleTouchEnd, { passive: false });

    this.sliderThumb.addEventListener('touchstart', handleTouchStart, { passive: false });
    this.sliderThumb.addEventListener('touchmove', handleTouchMove, { passive: false });
    this.sliderThumb.addEventListener('touchend', handleTouchEnd, { passive: false });
  }

  private setupJoystick(): void {
    if (!this.joystick || !this.joystickKnob) return;

    const joystickRect = () => this.joystick!.getBoundingClientRect();
    const maxDistance = 42; // Max distance knob can move from center

    // Store current joystick values for the interval
    let currentNormX = 0;
    let currentNormY = 0;

    const handleMove = (clientX: number, clientY: number) => {
      const rect = joystickRect();
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;

      let dx = clientX - centerX;
      let dy = clientY - centerY;

      // Clamp to circle
      const distance = Math.sqrt(dx * dx + dy * dy);
      if (distance > maxDistance) {
        dx = (dx / distance) * maxDistance;
        dy = (dy / distance) * maxDistance;
      }

      // Update knob position (visual feedback is linear)
      this.joystickKnob!.style.transform = `translate(${dx}px, ${dy}px)`;

      // Normalize to -1 to 1
      const normX = dx / maxDistance;
      const normY = dy / maxDistance;

      // Apply response curve for movement
      currentNormX = this.applyResponseCurve(normX);
      currentNormY = this.applyResponseCurve(normY);

      return { normX: currentNormX, normY: currentNormY };
    };

    const startJoystick = (e: PointerEvent) => {
      e.preventDefault();
      this.joystickActive = true;
      this.joystick!.classList.add('active');
      this.joystick!.setPointerCapture(e.pointerId);

      handleMove(e.clientX, e.clientY);

      // Continuous movement while held - uses stored values
      this.joystickInterval = window.setInterval(() => {
        if (this.joystickActive) {
          // Speed multiplier: 8 feels good with squared curve
          this.callbacks.onPositionChange?.(currentNormX * 8, -currentNormY * 8);
        }
      }, 16);
    };

    const moveJoystick = (e: PointerEvent) => {
      if (!this.joystickActive) return;
      e.preventDefault();

      // Update the stored values (interval will pick them up)
      handleMove(e.clientX, e.clientY);
    };

    const endJoystick = () => {
      this.joystickActive = false;
      this.joystick!.classList.remove('active');
      this.joystickKnob!.style.transform = 'translate(0, 0)';

      if (this.joystickInterval) {
        clearInterval(this.joystickInterval);
        this.joystickInterval = null;
      }
    };

    this.joystick.addEventListener('pointerdown', startJoystick);
    this.joystick.addEventListener('pointermove', moveJoystick);
    this.joystick.addEventListener('pointerup', endJoystick);
    this.joystick.addEventListener('pointercancel', endJoystick);
  }

  private setupRotationButtons(): void {
    const rotateCCW = this.hudElement?.querySelector('[data-action="rotate-ccw"]');
    const rotateCW = this.hudElement?.querySelector('[data-action="rotate-cw"]');

    // Rotation constants
    const TAP_ROTATION = Math.PI / 12;  // 15 degrees per tap
    const HOLD_ROTATION_RATE = Math.PI / 4;  // 45 degrees per second
    const HOLD_THRESHOLD = 200;  // ms before hold kicks in

    let holdInterval: number | null = null;
    let holdTimeout: number | null = null;
    let lastDoubleTapTime = 0;

    const startRotation = (direction: number, button: Element) => {
      // Immediate tap rotation
      this.callbacks.onRotationChange?.(TAP_ROTATION * direction);
      button.classList.add('active');

      // Set up hold for continuous rotation
      holdTimeout = window.setTimeout(() => {
        holdInterval = window.setInterval(() => {
          // 60fps continuous rotation
          this.callbacks.onRotationChange?.((HOLD_ROTATION_RATE / 60) * direction);
        }, 16);
      }, HOLD_THRESHOLD);
    };

    const endRotation = (button: Element) => {
      button.classList.remove('active');
      if (holdTimeout) {
        clearTimeout(holdTimeout);
        holdTimeout = null;
      }
      if (holdInterval) {
        clearInterval(holdInterval);
        holdInterval = null;
      }
    };

    // Check for double-tap to reset rotation
    const checkDoubleTap = (): boolean => {
      const now = Date.now();
      if (now - lastDoubleTapTime < 300) {
        // Double tap detected - reset rotation
        this.callbacks.onRotationChange?.(0);  // Signal reset (handled specially in main.ts)
        lastDoubleTapTime = 0;
        return true;
      }
      lastDoubleTapTime = now;
      return false;
    };

    // CCW button
    rotateCCW?.addEventListener('pointerdown', (e) => {
      e.preventDefault();
      if (!checkDoubleTap()) {
        startRotation(-1, rotateCCW);
      }
    });
    rotateCCW?.addEventListener('pointerup', () => endRotation(rotateCCW!));
    rotateCCW?.addEventListener('pointerleave', () => endRotation(rotateCCW!));
    rotateCCW?.addEventListener('pointercancel', () => endRotation(rotateCCW!));

    // CW button
    rotateCW?.addEventListener('pointerdown', (e) => {
      e.preventDefault();
      if (!checkDoubleTap()) {
        startRotation(1, rotateCW);
      }
    });
    rotateCW?.addEventListener('pointerup', () => endRotation(rotateCW!));
    rotateCW?.addEventListener('pointerleave', () => endRotation(rotateCW!));
    rotateCW?.addEventListener('pointercancel', () => endRotation(rotateCW!));
  }


  setOpacity(value: number): void {
    this.sliderValue = value;
    this.updateSliderVisual();
  }

  setLocked(locked: boolean): void {
    const btn = this.buttons.get('lock');
    if (btn) {
      btn.classList.toggle('active', locked);
      btn.innerHTML = locked ? this.getLockClosedSvg() : this.getLockOpenSvg();
    }
  }

  setGridEnabled(enabled: boolean): void {
    const btn = this.buttons.get('grid');
    btn?.classList.toggle('active', enabled);
  }

  setFilter(filter: FilterType): void {
    const btn = this.buttons.get('filter');
    btn?.setAttribute('data-filter', filter);
  }

  setStrobeEnabled(enabled: boolean): void {
    const btn = this.buttons.get('strobe');
    btn?.classList.toggle('active', enabled);
  }

  setFlipH(flipped: boolean): void {
    const btn = this.buttons.get('flipH');
    btn?.classList.toggle('active', flipped);
  }

  setFlipV(flipped: boolean): void {
    const btn = this.buttons.get('flipV');
    btn?.classList.toggle('active', flipped);
  }

  /**
   * Set drawing mode - hides all controls except opacity slider
   * Used when transitioning to DRAWING state for minimal distraction
   */
  setDrawingMode(enabled: boolean): void {
    if (!this.hudElement) return;
    this.hudElement.classList.toggle('drawing-mode', enabled);

    // Hide joystick and button tray in drawing mode
    if (this.tray) this.tray.style.display = enabled ? 'none' : '';
    if (this.joystick) this.joystick.style.display = enabled ? 'none' : '';

    // Hide scale and rotation controls
    const scaleControls = this.hudElement.querySelector('.hud-scale-controls') as HTMLElement;
    const rotationControls = this.hudElement.querySelector('.hud-rotation-controls') as HTMLElement;
    if (scaleControls) scaleControls.style.display = enabled ? 'none' : '';
    if (rotationControls) rotationControls.style.display = enabled ? 'none' : '';
  }

  /**
   * Set the visibility of HUD controls (joystick, tray, buttons)
   * The opacity slider and eye toggle remain visible
   */
  setControlsVisible(visible: boolean): void {
    if (this.controlsVisible === visible) return;
    this.toggleControlsVisibility();
  }

  /**
   * Get current visibility state of HUD controls
   */
  isControlsVisible(): boolean {
    return this.controlsVisible;
  }

  destroy(): void {
    if (this.joystickInterval) {
      clearInterval(this.joystickInterval);
      this.joystickInterval = null;
    }
    this.hudElement?.remove();
    this.hudElement = null;
    this.buttons.clear();
    this.sliderTrack = null;
    this.sliderThumb = null;
    this.tray = null;
    this.joystick = null;
    this.joystickKnob = null;
  }
}
